import BillingMethod from 'billing/models/BillingMethod'
import Coupon from 'billing/models/Coupon'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import Workspace from 'iam/models/Workspace'
import { PublishedLegend } from 'legends/PublishedLegend'
import { AnswerDTO } from '../dtos/questions'

/**
 * Possible draft change types.
 */
export type DraftChange =
  | WorkspaceSelected
  | OwnerSelected
  | SupportingClientSelected
  | TeamSelected
  | LegendSelected
  | AnswersChanged
  | Renamed
  | RequestedProviderChanged
  | CouponSelected
  | CouponCleared
  | BillingMethodSelected

/**
 * Workspace was selected.
 */
export interface WorkspaceSelected {
  type: 'WorkspaceSelected'
  workspace: Workspace | null
}

/**
 * Symbol representing an explicit selection of "myself".
 */
const TEAM_MYSELF = Symbol('Myself')

/**
 * Team selection options.
 */
export type TeamSelection = Team | typeof TEAM_MYSELF
export const TeamSelection = {
  /**
   * Explicitly no team; want to create job for myself.
   */
  myself: TEAM_MYSELF,
} as const

/**
 * Owner was selected.
 */
export interface OwnerSelected {
  type: 'OwnerSelected'
  owner: Member | null
}

/**
 * Supporting client was selected.
 */
export type SupportingClientSelected =
  | SupportingClientAdded
  | SupportingClientRemoved

/**
 * Supporting client was added.
 */
export interface SupportingClientAdded {
  type: 'SupportingClientAdded'
  member: Member
}

/**
 * Supporting client was removed.
 */
export interface SupportingClientRemoved {
  type: 'SupportingClientRemoved'
  member: Member
}

/**
 * Team was selected.
 */
export interface TeamSelected {
  type: 'TeamSelected'
  team: TeamSelection | null
}

/**
 * Legend was selected.
 */
export interface LegendSelected {
  type: 'LegendSelected'
  legend: PublishedLegend | null
}

/**
 * Answers were changed.
 */
export interface AnswersChanged {
  type: 'AnswersChanged'
  answers: AnswerDTO[]
}

/**
 * Job was renamed.
 */
export interface Renamed {
  type: 'Renamed'
  newName: string
}

/**
 * Explicitly chose "first available".
 */
const REQUESTED_PROVIDER_FIRST_AVAILABLE = Symbol('First Available')

/**
 * Requested provider selection.
 */
export type RequestedProviderSelection =
  | Member
  | typeof REQUESTED_PROVIDER_FIRST_AVAILABLE
export const RequestedProviderSelection = {
  /**
   * First available provider.
   */
  firstAvailable: REQUESTED_PROVIDER_FIRST_AVAILABLE,
} as const

/**
 * The requested provider was changed.
 */
export interface RequestedProviderChanged {
  type: 'RequestedProviderChanged'
  requestedProvider: RequestedProviderSelection | null
}

/**
 * A Coupon was selected.
 */
export interface CouponSelected {
  type: 'CouponSelected'
  coupon: Coupon
}

/**
 * The coupon was cleared.
 */
export interface CouponCleared {
  type: 'CouponCleared'
}

/**
 * The coupon was cleared.
 */
export interface BillingMethodSelected {
  type: 'BillingMethodSelected'
  billingMethod: BillingMethod
}
